/**
 * Day-plan discriminators. `planned_block_key` is opaque to the backend
 * (`event:<id>` / `deepwork:<epoch>` / `habit:<title>`); the web view parses
 * it client-side, so its prefixes live here rather than as inline literals.
 *
 * `EVENT` is a todo block-key prefix only — not the live Google/MS
 * `CalendarEvent` list (that comes from `/calendar/:platform/events`).
 */
export enum PlannedBlockKeyPrefix {
  EVENT = 'event',
  DEEP_WORK = 'deepwork',
  HABIT = 'habit'
}

/**
 * Namespace inside an `event:<id>` value for non-calendar sources.
 * Full key: `event:import:<uuid>` (prefix stays EVENT; import is part of the id).
 */
export enum DayPlanEventIdNamespace {
  IMPORT = 'import'
}

/** Origin of an Events-segment row (live calendar vs file import / series). */
export enum DayPlanEventSource {
  GOOGLE = 'google',
  MICROSOFT = 'microsoft',
  IMPORT = 'import',
  SERIES = 'series',
  UNKNOWN = 'unknown'
}

/** `POST /day-plan/import/file` detected recurrence on unscheduled/series drafts. */
export enum ImportEventRepeat {
  NONE = 'none',
  DAILY = 'daily',
  WEEKLY = 'weekly',
  FORTNIGHTLY = 'fortnightly'
}

/** RFC 5545 FREQ values used when building day-plan event-series RRULEs. */
export enum EventSeriesRRULEFreq {
  DAILY = 'DAILY',
  WEEKLY = 'WEEKLY'
}

/** RFC 5545 BYDAY tokens for WEEKLY RRULEs. */
export enum RRULEByDay {
  MO = 'MO',
  TU = 'TU',
  WE = 'WE',
  TH = 'TH',
  FR = 'FR',
  SA = 'SA',
  SU = 'SU'
}

/** Google Calendar event `status` values we care about in the Events UI. */
export enum GoogleCalendarEventStatus {
  CONFIRMED = 'confirmed',
  TENTATIVE = 'tentative',
  CANCELLED = 'cancelled'
}

/** Which timeline row a merged entry is, for the discriminated union. */
export enum TimelineEntryKind {
  EVENT = 'event',
  EVENT_CLUSTER = 'event_cluster',
  TASK = 'task',
  ROUTINE = 'routine',
  DEEP_WORK = 'deep_work'
}

/** Day header navigation intent. */
export enum DayNavDirection {
  PREV = 'prev',
  TODAY = 'today',
  NEXT = 'next'
}

/** Meta chips on a day-plan task row (TOP / due / created). */
export enum DayPlanTaskMetaKind {
  TOP = 'top',
  DUE = 'due',
  CREATED = 'created',
  TAGS = 'tags'
}

/** Schedule drawer opened from catalog Add vs planned Edit. */
export enum DayPlanScheduleDrawerMode {
  ADD = 'add',
  EDIT = 'edit'
}

/** File-import status modal: extract in progress, failed, or empty result. */
export enum FileImportStatusMode {
  EXTRACTING = 'extracting',
  FAILED = 'failed',
  EMPTY = 'empty'
}

/** Sentinel from `getExtractStatusPatch` when extract succeeded with events. */
export enum FileImportExtractPatch {
  CLEAR = 'clear'
}

/** Result kind when promoting an unscheduled import draft after times are filled. */
export enum UnscheduledPromoteKind {
  STAY = 'stay',
  EVENT = 'event',
  SERIES = 'series'
}

export enum IntegrationTab {
  PROJECT = 0,
  CALENDAR = 1,
  MCP = 2
}

/** BE routine progress status (`GET .../routine-progress`). */
export enum SEQUENCE_STATUS {
  COMPLETED = 'completed',
  POSTPONED = 'postponed',
  IN_PROGRESS = 'in_progress'
}

/** Client-derived habit row state on the Day Plan timeline. */
export enum HABIT_COMPLETION_STATUS {
  COMPLETED = 'completed',
  SKIPPED = 'skipped',
  PENDING = 'pending'
}

/**
 * Client-derived routine header status from scheduled habits vs ID lists.
 * Independent of BE `SEQUENCE_STATUS`.
 */
export enum DERIVED_ROUTINE_STATUS {
  PENDING = 'pending',
  STARTED = 'started',
  COMPLETED = 'completed'
}

/** FloatingWorkspace panel ids used on the day-plan route. */
export enum DayPlanFloatingWorkspacePanel {
  ADD_TASKS = 'add-tasks'
}

/** Segmented control values for Habits | Events. */
export enum DayPlanTodaySegment {
  HABITS = 'habits',
  EVENTS = 'events'
}
